import { CheckOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Company,
  CREATE_PITCH,
  ErrorResponse,
  GET_COMPANY,
  PATCH_COMPANY,
  Pitch,
  UploadedFile,
} from '@frontend/data-access';
import { FileUpload, supportedVideoFormats } from '@frontend/shared/ui';
import { App, Avatar, Button, Form } from 'antd';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';

interface FormValues {
  pitchVideo: UploadedFile;
}

function EditPitchForm({
  companyId,
  onFinish,
  onCancel,
}: {
  companyId?: number;
  onFinish: () => void;
  onCancel: () => void;
}) {
  const { notification } = App.useApp();
  const router = useRouter();
  const [isSaved, setIsSaved] = useState(false);
  const [form] = Form.useForm();
  const [isIncomplete, setIsIncomplete] = useState(false);
  const [state, setState] = useState<'idle' | 'uploading' | 'success'>('idle');
  const { data } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: companyId },
  });
  const { t } = useTranslation('common');

  const handleCompleted = () => {
    setIsSaved(true);
    notification.success({
      message: t('successfully_saved'),
    });
  };

  const handleError = (data: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: data.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const [patchCompany, { loading: loadingPatch }] = useMutation(PATCH_COMPANY, {
    onCompleted: handleCompleted,
    onError: handleError,
    refetchQueries: [{ query: GET_COMPANY, variables: { id: companyId } }],
  });

  const handleCompletedPitch = ({ createPitch }: { createPitch: Pitch }) => {
    router.push(`/pitch-arena/create/${createPitch.id}/basics`);
  };

  const [createPitch, { loading: createPitchLoading }] = useMutation(
    CREATE_PITCH,
    {
      onCompleted: handleCompletedPitch,
      onError: handleError,
    }
  );

  const handleOnFinish = async (values: FormValues) => {
    await patchCompany({
      variables: {
        input: {
          id: companyId,
          pitchVideos: [
            ...(data?.company?.pitchVideos ?? []),
            values.pitchVideo,
          ],
        },
      },
    });
  };

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsIncomplete(hasErrors);
  };

  const onClose = () => {
    setIsSaved(false);
  };

  const onClickPublish = () => {
    createPitch({
      variables: {
        input: {
          title: t('pitch_with_name', { name: data?.company.name }),
          body: `${data?.company.description}`,
          companyId: data?.company.id,
          topicIds: data?.company.topics?.map((t) => t.id),
          regionIds: data?.company.market?.regionIds,
          video: form.getFieldValue('pitchVideo'),
        },
      },
    });
  };

  const SuccessInfo = () => {
    return (
      <div className="text-center space-y-4 max-w-md m-auto">
        <Avatar size={68} className="!bg-green-500" icon={<CheckOutlined />} />
        <h3 className="text-gray-900 text-base font-medium">
          {t('successfully_uploaded')}
        </h3>
        <p className="text-gray-700 text-sm">{t('choose_share_pitch')}</p>
        <div className="grid grid-cols-2 gap-4">
          <Button shape="round" onClick={onClose} block>
            {t('close')}
          </Button>
          <Button
            shape="round"
            type="primary"
            onClick={onClickPublish}
            block
            loading={createPitchLoading}
            data-test="pitch-publish-btn"
          >
            {t('publish_in_pitches')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div>
      {isSaved ? (
        <SuccessInfo />
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFieldsChange={handleFormChange}
          onFinish={handleOnFinish}
        >
          <Form.Item
            name="pitchVideo"
            data-test="pitch-video-input"
            rules={[{ required: true, message: t('upload_file_required') }]}
          >
            <FileUpload
              id="pitch-video-input"
              description={<div className="mb-4">{t('click_drag_upload')}</div>}
              onUploadSuccess={(file) => {
                form.setFieldValue('pitchVideo', file);
                setState('success');
              }}
              compact={false}
              supportedFileTypes={supportedVideoFormats}
              maxFileSize={524288000}
              handleUploadLoading={(isLoading) => {
                if (isLoading) {
                  setState('uploading');
                } else {
                  setState('idle');
                }
              }}
              onRemove={() => {
                setState('idle');
                form.setFieldValue('pitchVideo', undefined);
              }}
            />
          </Form.Item>
          <div className="flex justify-end items-center gap-2">
            <Button disabled={loadingPatch} shape="round" onClick={onCancel}>
              {t('cancel')}
            </Button>
            <Button
              htmlType="submit"
              type="primary"
              shape="round"
              loading={loadingPatch}
              disabled={isIncomplete || state === 'uploading'}
            >
              {t('save')}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
}

export default EditPitchForm;
